import {
  CertificatesContexts,
  getVesselCertificates,
  VesselTypeCodes,
  type VesselTypeCode,
} from "@constants/certificates";
import { type VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import { FORM_FIELDS } from "@features/vessel-register/utils";
import type {
  AdditionalDocumentType,
  BunkerBargeCertificateType,
  BunkerBargeDocumentType,
  Vessel,
  VesselRequestPayload,
} from "@services/vessel/types";
import { toISODateStrict } from "@utils/formatters/form";

const BUNKER_BARGE_DOCUMENT_NAME_MAX_LENGTH = 50;

type BunkerBargeFormDocument = {
  name: string;
  url: string;
  type?: string;
  documentType?: string;
  otherSpecifyType?: string;
  documentName?: string;
  documentSummary?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
};

const normalizeBunkerBargeDocumentName = (name: string) =>
  name.slice(0, BUNKER_BARGE_DOCUMENT_NAME_MAX_LENGTH);

const getUploadedBunkerBargeDocuments = (
  documents?: BunkerBargeFormDocument[],
): BunkerBargeFormDocument[] =>
  (documents ?? []).filter((document) => Boolean(document.url && document.name));

const BUNKER_BARGE_CERTIFICATE_FIELD_CONFIG: {
  field: keyof VesselRegisterFormData;
  certificateType: BunkerBargeCertificateType;
  issueDateField?: keyof VesselRegisterFormData;
  expiryDateField?: keyof VesselRegisterFormData;
}[] = [
  {
    field: FORM_FIELDS.NAVIGATION_LICENSE,
    certificateType: "NAVIGATION_LICENSE",
    issueDateField: FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE,
    expiryDateField: FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
  },
  {
    field: FORM_FIELDS.CHARTER_PARTY_AGREEMENT,
    certificateType: "CHARTER_PARTY_AGREEMENT",
    issueDateField: FORM_FIELDS.CHARTER_START_DATE,
    expiryDateField: FORM_FIELDS.CHARTER_END_DATE,
  },
  {
    field: FORM_FIELDS.OIL_SPILL_COMPANY,
    certificateType: "OIL_SPILL_COMPANY",
    issueDateField: FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE,
    expiryDateField: FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
  },
  {
    field: FORM_FIELDS.SIRE_REPORT,
    certificateType: "SIRE_REPORT",
    issueDateField: FORM_FIELDS.SIRE_REPORT_FROM_DATE,
    expiryDateField: FORM_FIELDS.SIRE_REPORT_TO_DATE,
  },
  {
    field: FORM_FIELDS.HOSE_TEST_CERTIFICATE,
    certificateType: "HOSE_TEST_CERTIFICATE",
    expiryDateField: FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE,
  },
  {
    field: FORM_FIELDS.SHIP_TO_SHIP_PLAN,
    certificateType: "SHIP_TO_SHIP_PLAN",
    issueDateField: FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE,
    expiryDateField: FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE,
  },
];

const mapBunkerBargeCertificates = (formData: VesselRegisterFormData) =>
  BUNKER_BARGE_CERTIFICATE_FIELD_CONFIG.flatMap(
    ({ field, certificateType, issueDateField, expiryDateField }) =>
      getUploadedBunkerBargeDocuments(formData[field] as BunkerBargeFormDocument[] | undefined).map(
        (document) => {
          const issueDate = issueDateField
            ? toISODateStrict(formData[issueDateField] as Date | string)
            : "";
          const expiryDate = expiryDateField
            ? toISODateStrict(formData[expiryDateField] as Date | string)
            : "";

          return {
            certificateType,
            documentUrl: document.url,
            name: normalizeBunkerBargeDocumentName(document.name),
            ...(issueDate ? { issueDate } : {}),
            ...(expiryDate ? { expiryDate } : {}),
          };
        },
      ),
  );

const mapBunkerBargeAdditionalDocuments = (formData: VesselRegisterFormData) =>
  getUploadedBunkerBargeDocuments(formData.bunkerBargeAdditionalDocuments).map((document) => ({
    documentType: (document.documentType || "DOCUMENT") as BunkerBargeDocumentType,
    documentUrl: document.url,
    fileName: normalizeBunkerBargeDocumentName(document.name),
    documentName:
      document.documentName?.slice(0, 50) || normalizeBunkerBargeDocumentName(document.name),
    documentSummary: document.documentSummary?.slice(0, 300) || "",
    ...(document.documentType === "OTHER_SPECIFY"
      ? { otherSpecifyType: document.otherSpecifyType ?? "" }
      : {}),
    ...(document.issueDate ? { documentIssueDate: toISODateStrict(document.issueDate) } : {}),
    ...(document.expiryDate ? { documentExpiryDate: toISODateStrict(document.expiryDate) } : {}),
  }));

const mapVesselRegistrationFormToPayload = (
  formData: VesselRegisterFormData,
  existingVessel?: Vessel,
  isDraft?: boolean,
): VesselRequestPayload => {
  const certificates = getVesselCertificates(
    CertificatesContexts.BASE,
    formData.vesselTypeCode as VesselTypeCode,
    formData.grossTonnage,
  )
    .filter(({ type }) => {
      const certificate = formData[FORM_FIELDS.CERTIFICATES]?.[type];
      return (
        !!certificate?.certificateUrl ||
        (isDraft &&
          (certificate?.permanent ||
            certificate?.certificateExpiryDate ||
            certificate?.certificateUrl ||
            certificate?.certificateClassName))
      );
    })
    .map(({ type }) => {
      const certificate = formData[FORM_FIELDS.CERTIFICATES]?.[type];

      return {
        certificateType: type,
        certificateUrl: certificate?.certificateUrl ?? "",
        certificateDocumentName: certificate?.certificateDocumentName ?? "",
        certificateClassName: certificate?.certificateClassName ?? "",
        certificateExpiryDate: certificate?.permanent
          ? ""
          : certificate?.certificateExpiryDate
            ? toISODateStrict(certificate.certificateExpiryDate)
            : "",
        ...(isDraft ? { permanent: certificate?.permanent ?? false } : {}),
      };
    });

  const vesselType = formData.vesselTypeCode
    ? {
        ...(existingVessel?.vesselType ?? {}),
        vesselTypeCode: formData.vesselTypeCode,
      }
    : !isDraft
      ? (existingVessel?.vesselType ?? undefined)
      : undefined;

  const port = formData.portOfRegistration
    ? { ...(existingVessel?.port ?? {}), portCode: formData.portOfRegistration }
    : !isDraft
      ? (existingVessel?.port ?? undefined)
      : undefined;

  const country = formData.flag
    ? { ...(existingVessel?.country ?? {}), countryCode: formData.flag }
    : !isDraft
      ? (existingVessel?.country ?? undefined)
      : undefined;

  const vesselClassificationSociety = formData.vesselClassificationSociety
    ? {
        ...(existingVessel?.vesselClassificationSociety ?? {}),
        vesselClassificationSocietyCode: formData.vesselClassificationSociety,
      }
    : !isDraft
      ? (existingVessel?.vesselClassificationSociety ?? undefined)
      : undefined;

  const vesselCertificates =
    formData.vesselTypeCode === VesselTypeCodes.NAVY_VESSELS || certificates.length === 0
      ? []
      : certificates;
  return {
    ...(existingVessel ?? {}), // keep original fields if updating
    vesselName: formData.vesselName,
    officialNumber: formData.officialNumber?.trim() ? formData.officialNumber : null,
    vesselCallSign: formData.callSign?.trim() ? formData.callSign : null,
    ...(formData.noImoNumber ? {} : { vesselImoNumber: formData.imo }),
    ...(isDraft ? { noImo: formData.noImoNumber } : {}),
    loa: formData.lengthOverall,
    nrt: formData.netTonnage,
    grt: formData.grossTonnage,
    dwt: formData.deadweightTonnage,
    draftMax: formData.draftMax,
    shipBuildYear: formData.yearBuilt,
    dateOfDelivery: formData.dateOfDelivery ? toISODateStrict(formData.dateOfDelivery) : "",
    beam: formData.beam,
    bunkerRegistered: formData.bunkerRegistered,
    diplomaticVessel: formData.diplomaticVessel,
    remarks: formData.registrationRemarks ?? "",
    shipOwnership: {
      ...(existingVessel?.shipOwnership ?? {}),
      shipsOwner: formData.shipOwner,
      shipsCharterer: formData.shipCharterer,
      shipsManagement: formData.shipsManagement,
      shipsRegisteredOwner: formData.shipsRegisteredOwner,
      piClub: formData.piClub
        ? { piClubCode: formData.piClub, piClubName: formData.piClubOther?.slice(0, 150) }
        : undefined,
      mlcPiClub: formData.mlcClub
        ? { piClubCode: formData.mlcClub, piClubName: formData.mlcClubOther?.slice(0, 150) }
        : undefined,
    },
    vesselType,
    vesselCategory: existingVessel?.vesselCategory,
    port,
    country,
    vesselClassificationSociety,
    otherVesselClassificationSociety: formData.otherVesselClassificationSociety,
    vesselCertificates,
    vesselLogType: existingVessel?.vesselLogType ?? { vesselLogTypeCode: "CLEARTOOPERATE" },
    additionalDocuments: formData.additionalDocuments?.map((doc) => ({
      documentType: (doc.documentType || "DOCUMENT") as AdditionalDocumentType,
      documentUrl: doc.documentUrl,
      fileName: doc.fileName,
      documentName: doc.documentName?.slice(0, 50) || doc.fileName.slice(0, 50),
      documentSummary: doc.documentSummary?.slice(0, 300) || "",
      ...(doc.documentType === "OTHER_SPECIFY"
        ? { otherSpecifyType: doc.otherSpecifyType?.slice(0, 50) ?? "" }
        : {}),
      ...(doc.issueDate ? { documentIssueDate: toISODateStrict(doc.issueDate) } : {}),
      ...(doc.expiryDate ? { documentExpiryDate: toISODateStrict(doc.expiryDate) } : {}),
    })),
    ...(formData.bunkerRegistered
      ? {
          bunkerBargeDetails: {
            version: existingVessel?.bunkerBargeDetails?.version,
            natureOfServiceCode: formData.natureOfService!,
            bunkeringCompany: {
              companyCode: formData.bunkeringCompany!,
            },
            charterOwner: formData.registeredOwner!,
            oilCompany: {
              companyCode: formData.oilCompany!,
            },
            effectiveStartDate: formData.effectiveStartDate
              ? toISODateStrict(formData.effectiveStartDate)
              : "",
            effectiveEndDate: formData.effectiveEndDate
              ? toISODateStrict(formData.effectiveEndDate)
              : "",
            remarks: formData.remarks,
            companyNameHoseTestCertificate: formData.companyNameHoseTestCertificate,
            certificates: mapBunkerBargeCertificates(formData),
            additionalDocuments: mapBunkerBargeAdditionalDocuments(formData),
          },
        }
      : {}),
  };
};
export default mapVesselRegistrationFormToPayload;
